// First-touch campaign tags for signup attribution.
//
// The marketing site, this portal and the writer app report to three different
// PostHog projects, so PostHog's own `$initial_utm_*` can't follow a visitor from
// a tagged link to the account they create. The tags are kept here instead, from
// the first tagged arrival, and sent with the signup request; dabble-rest puts
// them on the new person in the app's PostHog project (and on the customer.io /
// intercom profile, like any other signup metadata).
//
// First touch wins: a later tagged visit doesn't overwrite an earlier one until
// it is older than the attribution window. The stored copy is cleared once an
// account is created, so a second account made on the same device doesn't
// inherit the first one's source.

const STORAGE_KEY = 'firstTouch';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
/** Matches the 90-day attribution window the PostHog projects use. */
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LENGTH = 100;

type UtmKey = (typeof UTM_KEYS)[number];

export type FirstTouch = Partial<Record<UtmKey, string>> & {
  landing_path: string;
  captured_at: number;
};

/** A tag value worth keeping: short, and never something that looks like an email. */
function cleanValue(value: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.includes('@')) return undefined;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

/** The stored first touch, or null when there is none or it has aged out. */
export function readFirstTouch(now = Date.now()): FirstTouch | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as FirstTouch;
    if (typeof stored?.captured_at !== 'number' || now - stored.captured_at > MAX_AGE_MS) return null;
    return stored;
  } catch {
    return null;
  }
}

/** Remember the URL's campaign tags, unless an earlier tagged arrival is still in the window. */
export function captureFirstTouch(url: URL, now = Date.now()): void {
  const tags: Partial<Record<UtmKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = cleanValue(url.searchParams.get(key));
    if (value) tags[key] = value;
  }
  if (!tags.utm_source) return;
  if (readFirstTouch(now)) return;

  const firstTouch: FirstTouch = { ...tags, landing_path: url.pathname, captured_at: now };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(firstTouch));
  } catch {
    // Storage blocked (private mode, quota): attribution is best-effort.
  }
}

export function clearFirstTouch(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** The first touch as flat `signup_*` signup metadata (customer.io/intercom attributes take flat values). */
export function firstTouchMetadata(now = Date.now()): Record<string, string> {
  const firstTouch = readFirstTouch(now);
  if (!firstTouch) return {};
  const metadata: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = firstTouch[key];
    if (value) metadata[`signup_${key}`] = value;
  }
  metadata.signup_landing_path = firstTouch.landing_path;
  return metadata;
}
